import { METRICS, type MetricDefinition } from './metrics';

/**
 * What each metric counts, in the words of the people who read the screen (UX-005). The
 * `formula` of METRICS stays the exact, technical definition for docs and audits; screens show
 * this text and never SQL, column names or status codes.
 */
export const METRIC_PLAIN: Partial<Record<keyof typeof METRICS, string>> = {
  requested: 'Todos los servicios pedidos en el período, por la hora de recogida (o de solicitud si no son programados).',
  completed: 'Servicios que terminaron el viaje.',
  cancelled: 'Servicios cancelados por el cliente, el conductor o la central, y los que el cliente no llegó a tomar.',
  unfulfilled: 'Servicios que ningún conductor aceptó a tiempo o que la central rechazó.',
  completion_rate: 'De cada 100 servicios pedidos, cuántos terminaron el viaje.',
  cancellation_rate: 'De cada 100 servicios pedidos, cuántos se cancelaron. Los que quedaron sin atender se cuentan aparte.',
  pending_now: 'Servicios que en este momento esperan conductor, sin contar los programados que aún no empiezan a buscar.',
  manual_share: 'De cada 100 servicios pedidos, cuántos necesitaron que alguien de la central asignara el conductor.',
  assign_time: 'Cuánto pasa desde que el cliente pide hasta que un conductor acepta. Solo servicios para ya, no programados.',
  pickup_time: 'Cuánto tarda el conductor desde que acepta hasta que llega al punto de recogida.',
  trip_time: 'Cuánto dura el viaje, desde que empieza hasta que termina.',
  total_time: 'Cuánto pasa desde que el cliente pide hasta que termina el viaje. Solo servicios para ya.',
  active_drivers: 'Conductores distintos que aceptaron al menos un servicio del período.',
  fleet_utilization: 'De los conductores conectados ahora, cuántos están ocupados en un servicio. Es solo el valor de este momento.',
  acceptance_rate: 'De cada 100 ofertas que los conductores alcanzaron a responder, cuántas aceptaron.',
  response_time: 'Cuánto tardan los conductores en aceptar o rechazar una oferta.',
  driver_cancellations: 'Servicios que el conductor canceló o soltó después de aceptarlos.',
  rating: 'Promedio de las calificaciones de 1 a 5 que dejaron los clientes en el período.',
  active_customers: 'Clientes distintos que pidieron al menos un servicio en el período, en cualquier estado.',
  new_customers: 'Clientes que pidieron su primer servicio en este período.',
  returning_customers: 'Clientes del período que ya habían pedido antes.',
  frequency: 'Cuántos servicios pidió, en promedio, cada cliente del período.',
  retention: 'De los clientes del período anterior, cuántos volvieron a pedir en este.',
  trip_value: 'Suma de las tarifas de los viajes terminados. Es lo que se cotizó al cliente, no dinero recibido por la plataforma.',
  avg_ticket: 'Valor promedio de un viaje terminado.',
  value_per_customer: 'Valor de los viajes terminados dividido entre los clientes que tuvieron al menos uno.',
  value_per_driver: 'Valor de los viajes terminados dividido entre los conductores que hicieron al menos uno.',
  quota_collected: 'Pagos de cuota registrados en el período, sin contar los anulados.',
  quota_pending: 'Conductores que hoy tienen la cuota vencida o en periodo de gracia.',
};

const BY_FORMULA = new Map<string, string>(
  Object.entries(METRICS).flatMap(([k, m]) => {
    const plain = METRIC_PLAIN[k as keyof typeof METRICS];
    return plain ? [[(m as MetricDefinition).formula, plain] as [string, string]] : [];
  }),
);

/** The plain text for a metric, or for a chart note that is a metric formula; other notes pass through. */
export function plainDefinition(m: MetricDefinition | string): string {
  const formula = typeof m === 'string' ? m : m.formula;
  return BY_FORMULA.get(formula) ?? (typeof m === 'string' ? m : m.question);
}
